import React, { useState, useEffect, useMemo } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { TabBar, TabBtn } from '../../components/common/Tabs';
import { operationsAPI } from '../../services/api';
import {
  HiOutlineClipboardCheck, HiOutlineDocumentText, HiOutlineExclamation,
  HiOutlinePlus, HiOutlineCloudUpload, HiOutlineSave, HiOutlineTrash, HiOutlineSearch,
  HiOutlineX, HiOutlineCheck, HiOutlineAdjustments, HiOutlineLightningBolt,
  HiOutlineArrowCircleRight, HiOutlineCube, HiOutlineClock, HiOutlineRefresh
} from 'react-icons/hi';
import toast from 'react-hot-toast';
import { io } from 'socket.io-client';
import Pagination from '../../components/common/Pagination';
import { formatMountingBracketCode } from '../../utils/productNameHelper';
import { getBuildProcessSpecs, formatBuildProcessSpecs } from '../../utils/buildProcessSpecs';
import { useAuth } from '../../context/AuthContext';

const SOCKET_URL = process.env.REACT_APP_API_URL || (window.location.port ? `${window.location.protocol}//${window.location.hostname}:9898` : window.location.origin);

// Clean incremental radii list in steps of 5: 5, 10, 15, 20 ... up to 700
const BENDING_RADII_OPTIONS = Array.from({ length: 140 }, (_, i) => String((i + 1) * 5));

const QualityDashboard = () => {
  const { user } = useAuth();
  const subIndustryId = user?.organizationId?.subIndustryId || 'drag_chain';
  const isDragChain = subIndustryId === 'drag_chain';

  const [activeTab, setActiveTab] = useState('production_qc'); // 'production_qc', 'qc_inspections', 'damaged_log', 'missed_log', 'invoice_portal'
  
  const [qualityLogs, setQualityLogs] = useState([]);
  const [productTemplates, setProductTemplates] = useState([]);
  const [buildJobs, setBuildJobs] = useState([]);
  const [buildFilter, setBuildFilter] = useState('pending'); // 'pending', 'all', 'inspected'
  const [buildPage, setBuildPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [qcPage, setQcPage] = useState(1);

  // Production Build QC Modal State
  const [showBuildQcModal, setShowBuildQcModal] = useState(false);
  const [selectedBuildJob, setSelectedBuildJob] = useState(null);
  const [buildQcAction, setBuildQcAction] = useState('pass'); // 'pass', 'fail', 'other'
  const [buildQcForm, setBuildQcForm] = useState({
    passedQuantity: 0,
    failedQuantity: 0,
    remarks: ''
  });

  // Form States
  const [qcForm, setQcForm] = useState({
    materialName: '',
    quantity: 1,
    unit: 'pcs',
    remarks: '',
    qcType: 'inspected',
    modelNumber: '',
    width: '',
    size: '',
    description: '',
    radius: '',
    chainType: 'OP',
    numberOfLinks: '',
    length: '',
    mountingBracketType: '',
    accessories: '',
    inspectionType: 'first_inspection',
    outcome: 'pass',
    dimensions: '',
    conduitClamp: '',
    reworkReason: ''
  });
  const [invoiceForm, setInvoiceForm] = useState({ materialName: '', quantity: '', unit: 'pcs', invoiceNumber: '', remarks: '', qcType: 'inspected', invoiceUrl: '' });
  const [showQcModal, setShowQcModal] = useState(false);

  const catalogModelNumbers = useMemo(() => {
    return Array.from(new Set(productTemplates.map(t => t.modelNumber).filter(Boolean)));
  }, [productTemplates]);

  // Widths & Links derived from catalog
  const availableWidths = useMemo(() => {
    if (!qcForm.modelNumber) {
      return Array.from(new Set(productTemplates.map(t => t.width || t.innerSize || t.size).filter(Boolean)));
    }
    const matches = productTemplates.filter(t => t.modelNumber === qcForm.modelNumber);
    const widths = Array.from(new Set(matches.map(t => t.width || t.innerSize || t.size).filter(Boolean)));
    return widths.length > 0 ? widths : Array.from(new Set(productTemplates.map(t => t.width || t.innerSize || t.size).filter(Boolean)));
  }, [productTemplates, qcForm.modelNumber]);

  const availableLinks = useMemo(() => {
    if (!qcForm.modelNumber) {
      return Array.from(new Set(productTemplates.map(t => t.numberOfLinks).filter(Boolean)));
    }
    const matches = productTemplates.filter(t => t.modelNumber === qcForm.modelNumber);
    const links = Array.from(new Set(matches.map(t => t.numberOfLinks).filter(Boolean)));
    return links.length > 0 ? links : Array.from(new Set(productTemplates.map(t => t.numberOfLinks).filter(Boolean)));
  }, [productTemplates, qcForm.modelNumber]);

  // Auto-compose spec string
  useEffect(() => {
    if (qcForm.modelNumber) {
      const parts = [
        qcForm.modelNumber,
        qcForm.width,
        qcForm.radius ? `R${qcForm.radius}` : '',
        qcForm.chainType,
        qcForm.numberOfLinks ? `${qcForm.numberOfLinks} Links` : '',
        qcForm.length,
        qcForm.mountingBracketType,
        qcForm.accessories
      ].filter(Boolean);
      const generatedName = `Drag Chain ${parts.join(' · ')}`;
      setQcForm(prev => ({
        ...prev,
        materialName: generatedName,
        size: prev.width || prev.size
      }));
    }
  }, [
    qcForm.modelNumber, qcForm.width, qcForm.radius,
    qcForm.chainType, qcForm.numberOfLinks, qcForm.length,
    qcForm.mountingBracketType, qcForm.accessories
  ]);

  const handleModelChange = (modelNum) => {
    if (!modelNum) {
      setQcForm(prev => ({
        ...prev,
        modelNumber: '',
        width: '',
        size: '',
        radius: '',
        chainType: 'OP',
        numberOfLinks: '',
        length: '',
        mountingBracketType: '',
        accessories: ''
      }));
      return;
    }
    const match = productTemplates.find(t => t.modelNumber === modelNum);
    const rawR = match?.radius ? String(match.radius).replace(/^R/i, '') : '';
    setQcForm(prev => ({
      ...prev,
      modelNumber: modelNum,
      width: match?.width || match?.innerSize || prev.width || '',
      size: match?.width || match?.innerSize || prev.size || '',
      radius: rawR || prev.radius || '',
      chainType: match?.chainType || prev.chainType || 'OP',
      numberOfLinks: match?.numberOfLinks || prev.numberOfLinks || '',
      length: match?.length || prev.length || '',
      mountingBracketType: match?.mountingBracketType || prev.mountingBracketType || '',
      accessories: match?.accessories || prev.accessories || ''
    }));
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [qcRes, tempRes, buildRes] = await Promise.all([
        operationsAPI.getQualityLogs(),
        operationsAPI.getProductTemplates(),
        operationsAPI.getBuildJobs()
      ]);
      setQualityLogs(qcRes.data.data || []);
      setProductTemplates(tempRes.data.data || []);
      setBuildJobs(buildRes.data.data || []);
    } catch (err) {
      toast.error('Failed to load quality reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchQualityLogs = fetchAllData;

  useEffect(() => {
    fetchAllData();

    let socket;
    if (user?.organizationId) {
      socket = io(SOCKET_URL, { withCredentials: true });
      socket.emit('join_org', user.organizationId);

      socket.on('build_updated', fetchAllData);
      socket.on('qc_updated', fetchAllData);
      socket.on('inventory_updated', fetchAllData);
    }
    return () => {
      if (socket) socket.disconnect();
    };
  }, [user?.organizationId]);

  const handleSaveQC = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = activeTab === 'invoice_portal' ? invoiceForm : qcForm;
      // Force qcType based on tab
      if (activeTab === 'damaged_log') payload.qcType = 'damaged';
      if (activeTab === 'missed_log') payload.qcType = 'missed';
      if (activeTab === 'qc_inspections') payload.qcType = 'inspected';

      await operationsAPI.createQualityLog(payload);
      toast.success('Quality check report uploaded and synced to Store Manager & Sales in real-time!');

      // If custom model logged, auto-save to product templates
      if (payload.modelNumber && activeTab !== 'invoice_portal') {
        const exists = productTemplates.some(t => t.modelNumber.toLowerCase() === payload.modelNumber.toLowerCase());
        if (!exists) {
          try {
            await operationsAPI.createProductTemplate({
              modelNumber: payload.modelNumber,
              name: payload.materialName,
              rack: 'Unassigned',
              unit: payload.unit || 'pcs',
              innerSize: 'N/A',
              radius: payload.radius || 'N/A',
              designType: payload.designType || 'N/A',
              vs: payload.vs || 'N/A',
              hs: payload.hs || 'N/A',
              sr: payload.sr || 'N/A',
              series: payload.series || 'QC Custom Additions'
            });
          } catch (tempErr) {
            console.error('Failed to auto-save product template:', tempErr);
          }
        }
      }

      setShowQcModal(false);
      
      // Reset Forms
      setQcForm({
        materialName: '',
        quantity: 1,
        unit: 'pcs',
        remarks: '',
        qcType: 'inspected',
        modelNumber: '',
        width: '',
        size: '',
        radius: '',
        chainType: 'OP',
        numberOfLinks: '',
        length: '',
        mountingBracketType: '',
        accessories: '',
        inspectionType: 'first_inspection',
        outcome: 'pass',
        dimensions: '',
        conduitClamp: '',
        reworkReason: ''
      });

      setInvoiceForm({ materialName: '', quantity: '', unit: 'pcs', invoiceNumber: '', remarks: '', qcType: 'inspected', invoiceUrl: '' });
      fetchQualityLogs();
    } catch (err) {
      toast.error('Failed to submit QC report');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteLog = async (id) => {
    if (!window.confirm('Are you sure you want to delete this log?')) return;
    try {
      await operationsAPI.deleteQualityLog(id);
      toast.success('Log deleted successfully');
      fetchQualityLogs();
    } catch (err) {
      toast.error('Failed to delete log');
    }
  };

  const triggerMockUpload = () => {
    // Generate a mock URL for invoice upload simulation
    const mockUrls = [
      'https://microsoft.com/en-us/mock-invoice-10023.pdf',
      'https://gcp.google.com/billing/invoice-992381.pdf',
      'https://azure.microsoft.com/billing/inv-448821.pdf'
    ];
    const randUrl = mockUrls[Math.floor(Math.random() * mockUrls.length)];
    setInvoiceForm({ ...invoiceForm, invoiceUrl: randUrl });
    toast.success('Document uploaded successfully (Simulated)');
  };

  // Filter logs based on active tab and search query
  const getFilteredLogs = () => {
    const typeMap = {
      qc_inspections: 'inspected',
      damaged_log: 'damaged',
      missed_log: 'missed',
      invoice_portal: 'inspected' // Show raw invoice inspections
    };
    const targetType = typeMap[activeTab];
    return qualityLogs.filter(log => {
      const matchType = log.qcType === targetType;
      const matchSearch = log.materialName.toLowerCase().includes(search.toLowerCase()) || 
                          (log.invoiceNumber && log.invoiceNumber.toLowerCase().includes(search.toLowerCase()));
      // If invoice portal, only show logs that actually contain an invoice number
      if (activeTab === 'invoice_portal') {
        return matchType && matchSearch && log.invoiceNumber;
      }
      return matchType && matchSearch;
    });
  };

  const filteredLogs = getFilteredLogs();

  // Completed production builds logic
  const completedBuilds = useMemo(() => {
    return (buildJobs || []).filter(job => job.status === 'completed');
  }, [buildJobs]);

  const pendingBuildsCount = useMemo(() => {
    return completedBuilds.filter(job => !job.qcStatus || job.qcStatus === 'pending_qc').length;
  }, [completedBuilds]);

  const passedBuildsCount = useMemo(() => {
    return completedBuilds.filter(job => job.qcStatus === 'passed').length;
  }, [completedBuilds]);

  const reworkBuildsCount = useMemo(() => {
    return completedBuilds.filter(job => job.qcStatus === 'failed' || job.qcStatus === 'partial').length;
  }, [completedBuilds]);

  const filteredBuilds = useMemo(() => {
    return completedBuilds.filter(job => {
      const isPending = !job.qcStatus || job.qcStatus === 'pending_qc';
      if (buildFilter === 'pending' && !isPending) return false;
      if (buildFilter === 'inspected' && isPending) return false;

      if (!search) return true;
      const q = search.toLowerCase();
      return (
        (job.modelNumber && job.modelNumber.toLowerCase().includes(q)) ||
        (job.productName && job.productName.toLowerCase().includes(q)) ||
        (job.machineName && job.machineName.toLowerCase().includes(q)) ||
        (job.productionLine && job.productionLine.toLowerCase().includes(q))
      );
    });
  }, [completedBuilds, buildFilter, search]);

  const handleOpenBuildQC = (job, actionType) => {
    setSelectedBuildJob(job);
    setBuildQcAction(actionType);
    const totalQty = Number(job.orderQuantity) || 0;
    if (actionType === 'pass') {
      setBuildQcForm({
        passedQuantity: totalQty,
        failedQuantity: 0,
        remarks: ''
      });
    } else if (actionType === 'fail') {
      setBuildQcForm({
        passedQuantity: 0,
        failedQuantity: totalQty,
        remarks: ''
      });
    } else {
      setBuildQcForm({
        passedQuantity: '',
        failedQuantity: '',
        remarks: ''
      });
    }
    setShowBuildQcModal(true);
  };

  const handleSwitchAction = (actionType) => {
    setBuildQcAction(actionType);
    const totalQty = Number(selectedBuildJob?.orderQuantity) || 0;
    if (actionType === 'pass') {
      setBuildQcForm(prev => ({
        ...prev,
        passedQuantity: totalQty,
        failedQuantity: 0
      }));
    } else if (actionType === 'fail') {
      setBuildQcForm(prev => ({
        ...prev,
        passedQuantity: 0,
        failedQuantity: totalQty
      }));
    } else {
      setBuildQcForm(prev => ({
        ...prev,
        passedQuantity: '',
        failedQuantity: ''
      }));
    }
  };

  const handleSubmitBuildQC = async (e) => {
    e.preventDefault();
    if (!buildQcForm.remarks || !buildQcForm.remarks.trim()) {
      toast.error('QC Remarks are mandatory and strictly required!');
      return;
    }
    const passed = Number(buildQcForm.passedQuantity) || 0;
    const failed = Number(buildQcForm.failedQuantity) || 0;
    const total = passed + failed;
    if (total === 0 && Number(selectedBuildJob.orderQuantity) > 0) {
      toast.error('Please enter valid passed or failed quantity.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await operationsAPI.processBuildJobQC(selectedBuildJob._id, {
        outcome: buildQcAction,
        passedQuantity: passed,
        failedQuantity: failed,
        remarks: buildQcForm.remarks.trim()
      });
      toast.success(res.data.message || 'Quality check processed and passed stock sent to Store!');
      setShowBuildQcModal(false);
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to process quality check');
    } finally {
      setSubmitting(false);
    }
  };

  const uniqueRadii = Array.from(new Set(productTemplates.map(t => t.radius).filter(Boolean))).filter(x => x !== 'N/A').sort();
  const uniqueVs = Array.from(new Set(productTemplates.map(t => t.vs).filter(Boolean))).filter(x => x !== 'N/A').sort();
  const uniqueHs = Array.from(new Set(productTemplates.map(t => t.hs).filter(Boolean))).filter(x => x !== 'N/A').sort();
  const uniqueSr = Array.from(new Set(productTemplates.map(t => t.sr).filter(Boolean))).filter(x => x !== 'N/A').sort();
  const uniqueSeriesList = Array.from(new Set(productTemplates.map(t => t.series).filter(Boolean))).filter(x => x !== 'N/A').sort();

  return (
    <DashboardLayout pageTitle="Quality Control & Inspection Workspace">
      
      {/* Tabs */}
      <TabBar>
        <TabBtn active={activeTab === 'production_qc'} onClick={() => setActiveTab('production_qc')} icon={<HiOutlineLightningBolt />}>
          Completed Builds QC ({pendingBuildsCount})
        </TabBtn>
        <TabBtn active={activeTab === 'qc_inspections'} onClick={() => setActiveTab('qc_inspections')} icon={<HiOutlineClipboardCheck />}>
          Goods QC Inspections
        </TabBtn>
        <TabBtn active={activeTab === 'damaged_log'} onClick={() => setActiveTab('damaged_log')} icon={<HiOutlineExclamation />}>
          Damaged Materials Log
        </TabBtn>
        <TabBtn active={activeTab === 'missed_log'} onClick={() => setActiveTab('missed_log')} icon={<HiOutlineExclamation />}>
          Missed/Short Items
        </TabBtn>
        <TabBtn active={activeTab === 'invoice_portal'} onClick={() => setActiveTab('invoice_portal')} icon={<HiOutlineDocumentText />}>
          OCR Invoice Upload
        </TabBtn>
      </TabBar>

      {/* 1. REALTIME PRODUCTION BUILDS QC VIEW */}
      {activeTab === 'production_qc' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Quick Metrics */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
            <div className="azure-card" style={{ padding: '16px 20px', borderLeft: '4px solid #f59e0b' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>
                Pending Quality Check
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#f59e0b', marginTop: '4px' }}>
                {pendingBuildsCount}
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginTop: '4px' }}>
                Awaiting Pass / Fail / Other action
              </div>
            </div>

            <div className="azure-card" style={{ padding: '16px 20px', borderLeft: '4px solid #10b981' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>
                Passed to Store & Sales
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#10b981', marginTop: '4px' }}>
                {passedBuildsCount}
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginTop: '4px' }}>
                Available in Unified Store Inventory
              </div>
            </div>

            <div className="azure-card" style={{ padding: '16px 20px', borderLeft: '4px solid #ef4444' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>
                Defective Rework Assigned
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#ef4444', marginTop: '4px' }}>
                {reworkBuildsCount}
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginTop: '4px' }}>
                Auto rebuild tasks for Supervisor
              </div>
            </div>

            <div className="azure-card" style={{ padding: '16px 20px', borderLeft: '4px solid var(--primary)' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>
                Total Completed Builds
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 700, color: 'var(--text-primary)', marginTop: '4px' }}>
                {completedBuilds.length}
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginTop: '4px' }}>
                Real-time factory build output
              </div>
            </div>
          </div>

          {/* Main Table Card */}
          <div className="azure-card" style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '1.15rem', color: 'var(--text-primary)', fontWeight: 600 }}>
                  Realtime Production Output for QC
                </h3>
                <span className="badge" style={{ background: 'rgba(59,130,246,0.15)', color: '#3b82f6', border: '1px solid rgba(59,130,246,0.3)', padding: '2px 8px', fontSize: '0.75rem', borderRadius: '12px' }}>
                  Live Sync Active
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <div className="form-input-icon" style={{ width: '260px' }}>
                  <HiOutlineSearch className="icon" />
                  <input
                    type="text"
                    className="form-input"
                    style={{ padding: '8px 12px 8px 36px', fontSize: '0.85rem' }}
                    placeholder="Search model, product, machine..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                </div>

                <div style={{ display: 'flex', background: 'rgba(255,255,255,0.05)', borderRadius: '6px', padding: '2px', border: '1px solid var(--border)' }}>
                  <button
                    type="button"
                    onClick={() => { setBuildFilter('pending'); setBuildPage(1); }}
                    style={{
                      padding: '6px 12px', fontSize: '0.8rem', fontWeight: 600, border: 'none', borderRadius: '4px', cursor: 'pointer',
                      background: buildFilter === 'pending' ? 'var(--primary)' : 'transparent',
                      color: buildFilter === 'pending' ? '#fff' : 'var(--text-dim)'
                    }}
                  >
                    Pending QC ({pendingBuildsCount})
                  </button>
                  <button
                    type="button"
                    onClick={() => { setBuildFilter('all'); setBuildPage(1); }}
                    style={{
                      padding: '6px 12px', fontSize: '0.8rem', fontWeight: 600, border: 'none', borderRadius: '4px', cursor: 'pointer',
                      background: buildFilter === 'all' ? 'var(--primary)' : 'transparent',
                      color: buildFilter === 'all' ? '#fff' : 'var(--text-dim)'
                    }}
                  >
                    All ({completedBuilds.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => { setBuildFilter('inspected'); setBuildPage(1); }}
                    style={{
                      padding: '6px 12px', fontSize: '0.8rem', fontWeight: 600, border: 'none', borderRadius: '4px', cursor: 'pointer',
                      background: buildFilter === 'inspected' ? 'var(--primary)' : 'transparent',
                      color: buildFilter === 'inspected' ? '#fff' : 'var(--text-dim)'
                    }}
                  >
                    Inspected ({completedBuilds.length - pendingBuildsCount})
                  </button>
                </div>
              </div>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-dim)' }}>
                <div className="spinner" style={{ margin: '0 auto 12px' }} /> Fetching live production builds...
              </div>
            ) : filteredBuilds.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-dim)' }}>
                <HiOutlineClipboardCheck style={{ fontSize: '3rem', margin: '0 auto 12px', opacity: 0.4 }} />
                <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-secondary)' }}>No completed production builds found.</div>
                <div style={{ fontSize: '0.85rem', marginTop: '6px', color: 'var(--text-dim)' }}>
                  {buildFilter === 'pending'
                    ? 'All completed builds have been QC verified and sent to Store!'
                    : 'Completed manufacturing orders will appear here automatically in real time.'}
                </div>
              </div>
            ) : (
              <>
                <div className="azure-table-container">
                  <table className="azure-table">
                    <thead>
                      <tr>
                        <th>Model Number</th>
                        <th>Product Name / Specs</th>
                        <th>Quantity Built</th>
                        <th>Machine / Line</th>
                        <th>Completed At</th>
                        <th>QC Status</th>
                        <th style={{ textAlign: 'center' }}>Quality Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBuilds.slice((buildPage - 1) * 10, buildPage * 10).map(job => {
                        const isPending = !job.qcStatus || job.qcStatus === 'pending_qc';
                        return (
                          <tr key={job._id} style={{ background: isPending ? 'rgba(245,158,11,0.03)' : 'transparent' }}>
                            <td>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                <span style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '0.92rem' }}>
                                  {job.modelNumber || 'N/A'}
                                </span>
                                {job.isRework && (
                                  <span style={{ fontSize: '0.72rem', color: '#ef4444', fontWeight: 700, background: 'rgba(239,68,68,0.12)', padding: '1px 6px', borderRadius: '4px', display: 'inline-block', width: 'fit-content' }}>
                                    ⚠️ REWORK BUILD
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>
                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{job.productName}</span>
                                <span style={{ fontSize: '0.78rem', color: 'var(--text-dim)' }}>
                                  {[job.productSize, job.radius && job.radius !== 'N/A' ? `R${job.radius}` : '', job.chainLength ? `${job.chainLength}mm` : ''].filter(Boolean).join(' · ')}
                                </span>
                              </div>
                            </td>
                            <td>
                              <span style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                                {job.orderQuantity} <span style={{ fontSize: '0.8rem', color: 'var(--text-dim)', fontWeight: 500 }}>{job.unit || 'pcs'}</span>
                              </span>
                            </td>
                            <td>
                              <div style={{ fontSize: '0.85rem' }}>
                                <div style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{job.machineName || 'Machine Auto'}</div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>{job.productionLine || 'Line 1'}</div>
                                {formatBuildProcessSpecs(job) && (
                                  <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', marginTop: '2px' }}>{formatBuildProcessSpecs(job)}</div>
                                )}
                              </div>
                            </td>
                            <td style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                              {job.completedAt ? new Date(job.completedAt).toLocaleString() : new Date(job.updatedAt).toLocaleString()}
                            </td>
                            <td>
                              {isPending ? (
                                <span style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                                  padding: '4px 10px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 700,
                                  background: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)'
                                }}>
                                  <HiOutlineClock /> Pending QC
                                </span>
                              ) : job.qcStatus === 'passed' ? (
                                <span style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                                  padding: '4px 10px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 700,
                                  background: 'rgba(16,185,129,0.15)', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)'
                                }}>
                                  <HiOutlineCheck /> Passed ({job.qcPassedQty || job.orderQuantity} {job.unit || 'pcs'})
                                </span>
                              ) : job.qcStatus === 'failed' ? (
                                <span style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                                  padding: '4px 10px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 700,
                                  background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)'
                                }}>
                                  <HiOutlineX /> Failed / Rework ({job.qcFailedQty || job.orderQuantity} {job.unit || 'pcs'})
                                </span>
                              ) : (
                                <span style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                                  padding: '4px 10px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 700,
                                  background: 'rgba(139,92,246,0.15)', color: '#8b5cf6', border: '1px solid rgba(139,92,246,0.3)'
                                }}>
                                  <HiOutlineAdjustments /> Split: {job.qcPassedQty || 0} Pass / {job.qcFailedQty || 0} Rework
                                </span>
                              )}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {isPending ? (
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                  <button
                                    type="button"
                                    onClick={() => handleOpenBuildQC(job, 'pass')}
                                    style={{
                                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                                      padding: '6px 12px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 700,
                                      background: '#10b981', color: '#ffffff', border: 'none', cursor: 'pointer',
                                      boxShadow: '0 2px 6px rgba(16,185,129,0.3)'
                                    }}
                                    title="All Passed: 100% Ok, ready to send to store"
                                  >
                                    <HiOutlineCheck /> Pass
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleOpenBuildQC(job, 'fail')}
                                    style={{
                                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                                      padding: '6px 12px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 700,
                                      background: '#ef4444', color: '#ffffff', border: 'none', cursor: 'pointer',
                                      boxShadow: '0 2px 6px rgba(239,68,68,0.3)'
                                    }}
                                    title="All Failed: Defective, assign rework rebuild to supervisor"
                                  >
                                    <HiOutlineX /> Fail
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleOpenBuildQC(job, 'other')}
                                    style={{
                                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                                      padding: '6px 12px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 700,
                                      background: '#8b5cf6', color: '#ffffff', border: 'none', cursor: 'pointer',
                                      boxShadow: '0 2px 6px rgba(139,92,246,0.3)'
                                    }}
                                    title="Other / Partial: Manually enter passed and failed quantities"
                                  >
                                    <HiOutlineAdjustments /> Other
                                  </button>
                                </div>
                              ) : (
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                  <button
                                    type="button"
                                    onClick={() => handleOpenBuildQC(job, job.qcStatus || 'other')}
                                    style={{
                                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                                      padding: '4px 10px', borderRadius: '4px', fontSize: '0.78rem', fontWeight: 600,
                                      background: 'rgba(255,255,255,0.06)', color: 'var(--text-secondary)', border: '1px solid var(--border)', cursor: 'pointer'
                                    }}
                                    title="Re-inspect / adjust this build QC"
                                  >
                                    <HiOutlineRefresh /> Re-Inspect
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <Pagination
                  currentPage={buildPage}
                  totalPages={Math.ceil(filteredBuilds.length / 10) || 1}
                  totalItems={filteredBuilds.length}
                  itemsPerPage={10}
                  onPageChange={setBuildPage}
                />
              </>
            )}
          </div>
        </div>
      )}

      {/* 2. GOODS & INVOICE INSPECTIONS (OTHER TABS) */}
      {activeTab !== 'production_qc' && (
      <div className="azure-card" style={{ padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div className="form-input-icon" style={{ width: '300px' }}>
            <HiOutlineSearch className="icon" />
            <input type="text" className="form-input" style={{ padding: '8px 12px 8px 36px', fontSize: '0.85rem' }} placeholder="Search items or invoices..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>

          <button className="btn btn-primary btn-sm" onClick={() => {
            setQcForm({
              materialName: '',
              quantity: 1,
              unit: 'pcs',
              remarks: '',
              qcType: 'inspected',
              modelNumber: '',
              width: '',
              size: '',
              radius: '',
              chainType: 'OP',
              numberOfLinks: '',
              length: '',
              mountingBracketType: '',
              accessories: '',
              inspectionType: 'first_inspection',
              outcome: 'pass',
              dimensions: '',
              conduitClamp: '',
              reworkReason: ''
            });

            setInvoiceForm({ materialName: '', quantity: '', unit: 'pcs', invoiceNumber: '', remarks: '', qcType: 'inspected', invoiceUrl: '' });
            setShowQcModal(true);
          }}>
            <HiOutlinePlus /> {activeTab === 'invoice_portal' ? 'Upload Invoice Bill' : 'Add Inspection Record'}
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-dim)' }}>
            <div className="spinner" style={{ margin: '0 auto 12px' }} /> Loading Quality Reports...
          </div>
        ) : filteredLogs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-dim)' }}>No quality logs found in this section.</div>
        ) : (
          <>
            <div className="azure-table-container">
              <table className="azure-table">
                <thead>
                  <tr>
                    <th>Material / Item</th>
                    <th>Insp. Type</th>
                    <th>Outcome</th>
                    <th>Quantity Checked</th>

                    {activeTab === 'invoice_portal' && <th>Invoice Number</th>}
                    {activeTab === 'invoice_portal' && <th>Document URL</th>}
                    <th>Inspection Status</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.slice((qcPage - 1) * 10, qcPage * 10).map(log => (
                    <tr key={log._id}>
                      <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{log.materialName}</td>
                      <td style={{ fontSize: '0.82rem', color: 'var(--text-dim)' }}>{log.inspectionType === 'final_inspection' ? '🔍 Final' : '🔎 First'}</td>
                      <td>
                        <span style={{
                          padding: '3px 10px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 700,
                          background: log.outcome === 'pass' ? 'rgba(34,197,94,0.15)' : log.outcome === 'hold' ? 'rgba(245,158,11,0.15)' : log.outcome === 'rework' ? 'rgba(59,130,246,0.15)' : 'rgba(239,68,68,0.15)',
                          color: log.outcome === 'pass' ? '#22c55e' : log.outcome === 'hold' ? '#f59e0b' : log.outcome === 'rework' ? '#3b82f6' : '#ef4444'
                        }}>
                          {log.outcome ? log.outcome.toUpperCase() : 'PASS'}
                        </span>
                      </td>
                      <td style={{ color: 'var(--primary-light)', fontWeight: 600 }}>{log.quantity} {log.unit}</td>

                      {activeTab === 'invoice_portal' && <td style={{ fontWeight: 600, color: 'var(--success)' }}>{log.invoiceNumber}</td>}
                      {activeTab === 'invoice_portal' && (
                        <td>
                          <a href={log.invoiceUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--accent-light)', textDecoration: 'underline', fontSize: '0.8rem' }}>
                            View Scan
                          </a>
                        </td>
                      )}
                      <td>
                        <span className={`azure-badge ${
                          log.status === 'pending_verification' ? 'warning' :
                          log.status === 'verified' ? 'success' : 'danger'
                        }`}>
                          {log.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{log.remarks || 'No notes.'}</td>
                      <td>
                        <button style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }} onClick={() => handleDeleteLog(log._id)}>
                          <HiOutlineTrash /> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={qcPage}
              totalPages={Math.ceil(filteredLogs.length / 10) || 1}
              totalItems={filteredLogs.length}
              itemsPerPage={10}
              onPageChange={setQcPage}
            />
          </>
        )}
      </div>
      )}

      {/* QC & INVOICE ENTRY MODAL */}
      {showQcModal && (
        <ModalOverlay onClose={() => setShowQcModal(false)} title={activeTab === 'invoice_portal' ? "OCR Bill / Invoice Upload Portal" : "New Quality Inspection Log"}>
          <form onSubmit={handleSaveQC}>
            {activeTab === 'invoice_portal' ? (
              
              /* INVOICE PORTAL FORM */
              <>
                <div className="form-group">
                  <label className="form-label">Invoice / Bill Number *</label>
                  <input type="text" className="form-input" required value={invoiceForm.invoiceNumber} onChange={e => setInvoiceForm({ ...invoiceForm, invoiceNumber: e.target.value })} placeholder="e.g. INV-2026-001" />
                </div>
                <div className="form-group">
                  <label className="form-label">Material Inward Name *</label>
                  <input type="text" className="form-input" required value={invoiceForm.materialName} onChange={e => setInvoiceForm({ ...invoiceForm, materialName: e.target.value })} placeholder="e.g. Nylon Drag Chains STR-35" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">Billed Qty *</label>
                    <input type="number" step="0.01" className="form-input" required value={invoiceForm.quantity} onChange={e => setInvoiceForm({ ...invoiceForm, quantity: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Unit *</label>
                    <select className="form-input" value={invoiceForm.unit} onChange={e => setInvoiceForm({ ...invoiceForm, unit: e.target.value })}>
                      <option value="pcs">pcs</option>
                      <option value="kg">kg</option>
                      <option value="meters">meters</option>
                      <option value="boxes">boxes</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Upload Scan Invoice (PDF/Image) *</label>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button type="button" className="btn btn-secondary" onClick={triggerMockUpload} style={{ flex: 1 }}>
                      <HiOutlineCloudUpload /> Simulate Scan Document
                    </button>
                    {invoiceForm.invoiceUrl && <span style={{ color: 'var(--success)', alignSelf: 'center', fontSize: '0.8rem' }}>Uploaded ✅</span>}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Invoice Remarks</label>
                  <textarea className="form-input" rows="2" value={invoiceForm.remarks} onChange={e => setInvoiceForm({ ...invoiceForm, remarks: e.target.value })} placeholder="Verify details..."></textarea>
                </div>
              </>

            ) : (

              /* INSPECTION LOG FORM */
              <>
                {/* 1. Product Name — always first */}
                <div className="form-group">
                  <label className="form-label">Product Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    required
                    value={qcForm.materialName}
                    onChange={e => setQcForm({ ...qcForm, materialName: e.target.value })}
                    placeholder="e.g. Nylon Drag Chain"
                  />
                </div>

                {/* 1b. Inspection Stage + Outcome */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label" style={{ fontSize: '0.8rem' }}>Inspection Stage</label>
                    <select className="form-input" value={qcForm.inspectionType} onChange={e => setQcForm({ ...qcForm, inspectionType: e.target.value })}>
                      <option value="first_inspection">🔎 First Inspection</option>
                      <option value="final_inspection">🔍 Final Inspection</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label" style={{ fontSize: '0.8rem' }}>Outcome</label>
                    <select className="form-input" value={qcForm.outcome} onChange={e => setQcForm({ ...qcForm, outcome: e.target.value })}
                      style={{
                        color: qcForm.outcome === 'pass' ? '#22c55e' : qcForm.outcome === 'hold' ? '#f59e0b' : qcForm.outcome === 'rework' ? '#3b82f6' : '#ef4444',
                        fontWeight: 700
                      }}>
                      <option value="pass">✅ PASS</option>
                      <option value="hold">⏸️ HOLD</option>
                      <option value="reject">❌ REJECT</option>
                      <option value="rework">🔄 REWORK</option>
                    </select>
                  </div>
                </div>
                {qcForm.outcome === 'rework' && (
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: '0.8rem' }}>Rework Reason</label>
                    <input className="form-input" value={qcForm.reworkReason} onChange={e => setQcForm({ ...qcForm, reworkReason: e.target.value })} placeholder="Describe what needs rework..." />
                  </div>
                )}

                {/* DRAG CHAIN CONFIGURATION SECTION — DB-driven dropdowns */}
                {isDragChain && (
                  <div style={{ padding: '16px', background: 'rgba(249,115,22,0.03)', borderRadius: '10px', border: '1px solid rgba(249,115,22,0.2)', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                      <span style={{ fontSize: '1.1rem' }}>⚙️</span>
                      <h3 style={{ fontSize: '0.9rem', fontWeight: 700, margin: 0, color: 'var(--primary)' }}>
                        Drag Chain Configuration (PDF Specification)
                      </h3>
                      {catalogModelNumbers.length === 0 && (
                        <span style={{ fontSize: '0.72rem', color: '#f59e0b', marginLeft: 'auto' }}>
                          ⚠️ No catalog data — add product templates first
                        </span>
                      )}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>

                      {/* 1. Model Number — from DB */}
                      <div className="form-group">
                        <label className="form-label" style={{ fontSize: '0.78rem' }}>Model No. *</label>
                        <select
                          className="form-input"
                          style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                          value={qcForm.modelNumber}
                          onChange={e => handleModelChange(e.target.value)}
                        >
                          <option value="">-- Select Model --</option>
                          {catalogModelNumbers.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                      </div>

                      {/* 2. Width (Inner Width) — cascades from Model */}
                      <div className="form-group">
                        <label className="form-label" style={{ fontSize: '0.78rem' }}>Width (Inner)</label>
                        <select
                          className="form-input"
                          style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                          value={qcForm.width}
                          onChange={e => setQcForm(p => ({ ...p, width: e.target.value }))}
                        >
                          <option value="">-- Select Width --</option>
                          {availableWidths.map(w => <option key={w} value={w}>{w}</option>)}
                        </select>
                      </div>

                      {/* 3. Bending Radius — 5, 10, 15, 20 ... up to 700 */}
                      <div className="form-group">
                        <label className="form-label" style={{ fontSize: '0.78rem' }}>Bending Radius</label>
                        <select
                          className="form-input"
                          style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                          value={qcForm.radius?.replace(/^R/i, '') || ''}
                          onChange={e => setQcForm(p => ({ ...p, radius: e.target.value }))}
                        >
                          <option value="">-- Select Radius --</option>
                          {BENDING_RADII_OPTIONS.map(r => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                      </div>

                      {/* 4. Type: OP / CP / SEMI */}
                      <div className="form-group">
                        <label className="form-label" style={{ fontSize: '0.78rem' }}>Type (Open/Closed)</label>
                        <select
                          className="form-input"
                          style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                          value={qcForm.chainType || 'OP'}
                          onChange={e => setQcForm(p => ({ ...p, chainType: e.target.value }))}
                        >
                          <option value="OP">OP — Open</option>
                          <option value="CP">CP — Closed</option>
                          <option value="SEMI">SEMI — Semi-Closed</option>
                        </select>
                      </div>

                      {/* 5. Number of Links per meter — from DB */}
                      <div className="form-group">
                        <label className="form-label" style={{ fontSize: '0.78rem' }}>No. of Links (per m)</label>
                        <select
                          className="form-input"
                          style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                          value={qcForm.numberOfLinks}
                          onChange={e => setQcForm(p => ({ ...p, numberOfLinks: e.target.value }))}
                        >
                          <option value="">-- Select Links --</option>
                          {availableLinks.length > 0
                            ? availableLinks.map(l => <option key={l} value={l}>{l}</option>)
                            : ['18', '20', '22', '24', '26', '28', '30'].map(l => <option key={l} value={l}>{l}</option>)
                          }
                        </select>
                      </div>

                      {/* 6. Length */}
                      <div className="form-group">
                        <label className="form-label" style={{ fontSize: '0.78rem' }}>Length (mm / m)</label>
                        <select
                          className="form-input"
                          style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                          value={qcForm.length}
                          onChange={e => setQcForm(p => ({ ...p, length: e.target.value }))}
                        >
                          <option value="">-- Select Length --</option>
                          {['500 MM', '1000 MM', '1500 MM', '2000 MM', '2500 MM', '3000 MM', '4000 MM', '5000 MM'].map(l => (
                            <option key={l} value={l}>{l}</option>
                          ))}
                        </select>
                      </div>

                      {/* 7. Mounting Bracket Type */}
                      <div className="form-group">
                        <label className="form-label" style={{ fontSize: '0.78rem' }}>Mounting Bracket Type</label>
                        <select
                          className="form-input"
                          style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                          value={qcForm.mountingBracketType}
                          onChange={e => setQcForm(p => ({ ...p, mountingBracketType: e.target.value }))}
                        >
                          <option value="">-- Select Bracket --</option>
                          <option value="B-BOX">B-BOX Type ({formatMountingBracketCode('B-BOX', qcForm.modelNumber || '35.1')})</option>
                          <option value="L-ANGULAR">L-ANGULAR Type ({formatMountingBracketCode('L-ANGULAR', qcForm.modelNumber || '35.1')})</option>
                          <option value="LSR(P)">LSR (P) — Plastic Angular Strain Relief ({formatMountingBracketCode('LSR(P)', qcForm.modelNumber || '35.1')})</option>
                          <option value="LSR(M)">LSR (M) — Metal Angular Strain Relief ({formatMountingBracketCode('LSR(M)', qcForm.modelNumber || '35.1')})</option>
                          <option value="SRP-25">SRP-25</option>
                          <option value="SRP-35">SRP-35</option>
                          <option value="SRP-50">SRP-50</option>
                        </select>
                      </div>

                      {/* 8. Accessories / Separator */}
                      <div className="form-group">
                        <label className="form-label" style={{ fontSize: '0.78rem' }}>Accessories (Separator)</label>
                        <select
                          className="form-input"
                          style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                          value={qcForm.accessories}
                          onChange={e => setQcForm(p => ({ ...p, accessories: e.target.value }))}
                        >
                          <option value="">-- None / Not Required --</option>
                          <option value="HS">HS — Horizontal Separator</option>
                          <option value="VS">VS — Vertical Separator</option>
                          <option value="HS+VS">HS + VS (Both)</option>
                        </select>
                      </div>

                    </div>

                    {/* Live Spec Preview */}
                    {qcForm.materialName && (
                      <div style={{ marginTop: '12px', padding: '8px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: '6px', fontSize: '0.8rem', color: 'var(--text-dim)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>📋 Inspection Spec:</span>
                        <strong style={{ color: 'var(--primary)' }}>{qcForm.materialName}</strong>
                      </div>
                    )}
                  </div>
                )}

                {/* 5. Quantity + Unit */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">Inspected Quantity (Pieces / pcs) *</label>
                    <input type="number" step="0.01" className="form-input" required value={qcForm.quantity} onChange={e => setQcForm({ ...qcForm, quantity: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Unit *</label>
                    <select className="form-input" value={qcForm.unit} onChange={e => setQcForm({ ...qcForm, unit: e.target.value })}>
                      <option value="pcs">pcs (Pieces)</option>
                      <option value="kg">kg (Kilograms)</option>
                      <option value="meters">meters (Meters)</option>
                      <option value="boxes">boxes (Boxes)</option>
                    </select>
                  </div>
                </div>

                {/* 6. Remarks */}
                <div className="form-group">
                  <label className="form-label">QC Notes / Diagnostic Remarks</label>
                  <textarea className="form-input" rows="2" value={qcForm.remarks} onChange={e => setQcForm({ ...qcForm, remarks: e.target.value })} placeholder="State details of damages, shortages, or discrepancies..."></textarea>
                </div>
              </>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button type="button" className="btn btn-cancel" onClick={() => setShowQcModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting || (activeTab === 'invoice_portal' && !invoiceForm.invoiceUrl)}>
                <HiOutlineSave /> {submitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        </ModalOverlay>
      )}

      {/* BUILD QC MODAL (PASS / FAIL / OTHER -> SEND TO STORE) */}
      {showBuildQcModal && selectedBuildJob && (
        <ModalOverlay onClose={() => setShowBuildQcModal(false)} title={`Quality Check Inspection — ${selectedBuildJob.modelNumber || selectedBuildJob.productName}`}>
          <form onSubmit={handleSubmitBuildQC}>
            {/* Top Product Summary */}
            <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '10px', padding: '14px 16px', marginBottom: '18px', border: '1px solid var(--border)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Model Number</span>
                  <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--primary)', marginTop: '2px' }}>
                    {selectedBuildJob.modelNumber || 'N/A'}
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Built Output</span>
                  <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)', marginTop: '2px' }}>
                    {selectedBuildJob.orderQuantity} {selectedBuildJob.unit || 'pcs'}
                  </div>
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Product & Specifications</span>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)', marginTop: '2px' }}>
                    {selectedBuildJob.productName} · {selectedBuildJob.productSize || 'Standard'} {selectedBuildJob.radius && selectedBuildJob.radius !== 'N/A' ? `· R${selectedBuildJob.radius}` : ''}
                  </div>
                </div>
                {getBuildProcessSpecs(selectedBuildJob).map(spec => (
                  <div key={spec.label}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{spec.label}</span>
                    <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginTop: '2px' }}>{spec.value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Type Selector */}
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label className="form-label" style={{ fontWeight: 600 }}>Inspection Outcome Selection</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => handleSwitchAction('pass')}
                  style={{
                    padding: '10px', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 700,
                    border: buildQcAction === 'pass' ? '2px solid #10b981' : '1px solid var(--border)',
                    background: buildQcAction === 'pass' ? 'rgba(16,185,129,0.18)' : 'rgba(255,255,255,0.03)',
                    color: buildQcAction === 'pass' ? '#10b981' : 'var(--text-dim)',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                  }}
                >
                  <HiOutlineCheck /> Pass (All Ok)
                </button>
                <button
                  type="button"
                  onClick={() => handleSwitchAction('fail')}
                  style={{
                    padding: '10px', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 700,
                    border: buildQcAction === 'fail' ? '2px solid #ef4444' : '1px solid var(--border)',
                    background: buildQcAction === 'fail' ? 'rgba(239,68,68,0.18)' : 'rgba(255,255,255,0.03)',
                    color: buildQcAction === 'fail' ? '#ef4444' : 'var(--text-dim)',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                  }}
                >
                  <HiOutlineX /> Fail (Defect)
                </button>
                <button
                  type="button"
                  onClick={() => handleSwitchAction('other')}
                  style={{
                    padding: '10px', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 700,
                    border: buildQcAction === 'other' ? '2px solid #8b5cf6' : '1px solid var(--border)',
                    background: buildQcAction === 'other' ? 'rgba(139,92,246,0.18)' : 'rgba(255,255,255,0.03)',
                    color: buildQcAction === 'other' ? '#8b5cf6' : 'var(--text-dim)',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                  }}
                >
                  <HiOutlineAdjustments /> Other (Split)
                </button>
              </div>
            </div>

            {/* Quantities Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label" style={{ fontWeight: 600, color: '#10b981' }}>
                  Passed Quantity ({selectedBuildJob.unit || 'pcs'})
                </label>
                <input
                  type="number"
                  min="0"
                  max={selectedBuildJob.orderQuantity}
                  className="form-input"
                  required
                  value={buildQcForm.passedQuantity}
                  onChange={e => {
                    const val = e.target.value === '' ? '' : Math.max(0, Number(e.target.value));
                    setBuildQcForm(prev => {
                      const total = Number(selectedBuildJob.orderQuantity) || 0;
                      const newPassed = val === '' ? 0 : val;
                      const autoFailed = buildQcAction !== 'other' ? Math.max(0, total - newPassed) : prev.failedQuantity;
                      return { ...prev, passedQuantity: val, failedQuantity: autoFailed };
                    });
                  }}
                  placeholder="0"
                  style={{ borderColor: '#10b981' }}
                />
                <div style={{ fontSize: '0.72rem', color: '#10b981', marginTop: '4px' }}>
                  ✅ Will be sent to Store & Sales inventory
                </div>
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label" style={{ fontWeight: 600, color: '#ef4444' }}>
                  Failed Quantity ({selectedBuildJob.unit || 'pcs'})
                </label>
                <input
                  type="number"
                  min="0"
                  max={selectedBuildJob.orderQuantity}
                  className="form-input"
                  required
                  value={buildQcForm.failedQuantity}
                  onChange={e => {
                    const val = e.target.value === '' ? '' : Math.max(0, Number(e.target.value));
                    setBuildQcForm(prev => {
                      const total = Number(selectedBuildJob.orderQuantity) || 0;
                      const newFailed = val === '' ? 0 : val;
                      const autoPassed = buildQcAction !== 'other' ? Math.max(0, total - newFailed) : prev.passedQuantity;
                      return { ...prev, failedQuantity: val, passedQuantity: autoPassed };
                    });
                  }}
                  placeholder="0"
                  style={{ borderColor: '#ef4444' }}
                />
                <div style={{ fontSize: '0.72rem', color: '#ef4444', marginTop: '4px' }}>
                  ⚠️ Auto re-assigned to Supervisor as rebuild order
                </div>
              </div>
            </div>

            {/* Remark (Required) */}
            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label className="form-label" style={{ fontWeight: 600, display: 'flex', justifyContent: 'space-between' }}>
                <span>Quality Inspection Remarks <span style={{ color: '#ef4444' }}>* (Required)</span></span>
                {buildQcForm.remarks.trim().length === 0 && (
                  <span style={{ color: '#ef4444', fontSize: '0.75rem', fontWeight: 500 }}>Mandatory to proceed</span>
                )}
              </label>
              <textarea
                className="form-input"
                rows="3"
                required
                value={buildQcForm.remarks}
                onChange={e => setBuildQcForm({ ...buildQcForm, remarks: e.target.value })}
                placeholder="Enter QC remarks, e.g.: Dimensions verified as per specs, pin movement smooth, link flexibility ok, or reason for rejection/rework..."
                style={{
                  border: !buildQcForm.remarks.trim() ? '1px solid rgba(239,68,68,0.5)' : '1px solid var(--border)'
                }}
              />
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '14px', borderTop: '1px solid var(--border)' }}>
              <button
                type="button"
                className="btn btn-cancel"
                onClick={() => setShowBuildQcModal(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-success"
                disabled={submitting || !buildQcForm.remarks.trim()}
                style={{
                  background: !buildQcForm.remarks.trim() ? 'rgba(16,185,129,0.4)' : '#10b981',
                  color: '#ffffff',
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 20px',
                  cursor: !buildQcForm.remarks.trim() ? 'not-allowed' : 'pointer'
                }}
              >
                <HiOutlineArrowCircleRight style={{ fontSize: '1.2rem' }} />
                {submitting ? 'Sending to Store...' : 'Send to Store'}
              </button>
            </div>
          </form>
        </ModalOverlay>
      )}

    </DashboardLayout>
  );
};


const ModalOverlay = ({ children, onClose, title }) => (
  <div onClick={(e) => e.target === e.currentTarget && onClose()} style={{
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
  }}>
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px',
      width: '100%', maxWidth: '520px', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
      overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.5)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <h3 style={{ fontSize: '1.2rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>{title}</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', fontSize: '1.4rem', cursor: 'pointer' }}><HiOutlineX /></button>
      </div>
      <div style={{ padding: '24px', overflowY: 'auto', overflowX: 'hidden', flex: 1 }}>
        {children}
      </div>
    </div>
  </div>
);

export default QualityDashboard;
